import { useEffect, useRef } from 'react'
import type { VocabularyState } from '../types/daily'

export interface VocabularyDialogState {
  status: 'loading' | 'resolved' | 'offline' | 'not_found'
  term: string
  contextExcerpt?: string
  vocabulary?: VocabularyState
}

interface VocabularyDialogProps {
  lookup?: VocabularyDialogState
  onClose: () => void
  onToggleSaved: (saved: boolean) => void
  onSavePending: () => void
  onRetry: () => void
}

const difficultyLabel = (difficulty: VocabularyState['difficulty']) => difficulty === 'unrated' ? '未标级' : difficulty

export function VocabularyDialog({ lookup, onClose, onToggleSaved, onSavePending, onRetry }: VocabularyDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (lookup && dialog && !dialog.open) dialog.showModal()
    if (!lookup && dialog?.open) dialog.close()
  }, [lookup])

  if (!lookup) return null
  const vocabulary = lookup.vocabulary

  return (
    <dialog ref={dialogRef} className="vocabulary-dialog" onClose={onClose} onCancel={onClose}>
      <div className="dialog-handle" aria-hidden="true" />
      <button type="button" className="dialog-close" onClick={onClose} aria-label="关闭词汇解释">×</button>
      {lookup.status === 'loading' && <div className="vocabulary-dialog__status" role="status"><span className="lookup-spinner" /><p>正在查找 “{lookup.term}” 的释义…</p></div>}
      {lookup.status === 'resolved' && vocabulary && <>
        <p className="eyebrow">{difficultyLabel(vocabulary.difficulty)}{vocabulary.partOfSpeech ? ` · ${vocabulary.partOfSpeech}` : ''}</p>
        <h2>{vocabulary.term}</h2>
        {vocabulary.phonetic && <p className="vocabulary-dialog__phonetic" lang="en">/{vocabulary.phonetic.replace(/^\/+|\/+$/g, '')}/</p>}
        <p className="vocabulary-dialog__meaning">{vocabulary.meaningZh}</p>
        <dl>
          <div>
            <dt>在本文中</dt>
            <dd>{vocabulary.meaningInContextZh}</dd>
          </div>
          {lookup.contextExcerpt && <div>
            <dt>选中语境</dt>
            <dd lang="en">{lookup.contextExcerpt}</dd>
          </div>}
          {vocabulary.collocation && <div>
            <dt>常见搭配</dt>
            <dd lang="en">{vocabulary.collocation}</dd>
          </div>}
        </dl>
        <button type="button" className={`primary-button${vocabulary.saved ? ' is-selected' : ''}`} onClick={() => onToggleSaved(!vocabulary.saved)}>
          {vocabulary.saved ? '已收藏此表达' : '收藏此表达'}
        </button>
      </>}
      {(lookup.status === 'offline' || lookup.status === 'not_found') && <>
        <p className="eyebrow">{lookup.status === 'offline' ? '暂时离线' : '词典未收录'}</p>
        <h2>{lookup.term}</h2>
        <p className="vocabulary-dialog__meaning">{lookup.status === 'offline'
          ? '本设备尚未缓存对应词典分片。联网后可重试，也可以先保存选中语境。'
          : '暂时没有找到可靠释义。你可以先保存，之后在词汇本中重试。'}</p>
        {lookup.contextExcerpt && <dl><div><dt>选中语境</dt><dd lang="en">{lookup.contextExcerpt}</dd></div></dl>}
        <div className="vocabulary-dialog__actions">
          <button type="button" className="primary-button" onClick={onSavePending}>保存待补充</button>
          <button type="button" onClick={onRetry}>重新查词</button>
        </div>
      </>}
    </dialog>
  )
}
